import { useState, useEffect, useMemo } from 'react';
import { useAnimateOnDataChange } from '../shared/useAnimateOnDataChange';
import { useChartContainerWidth } from '../shared/useChartContainerWidth';
import { 
    Box, Skeleton, Text, Flex, Button, Icon, Menu, MenuButton, MenuList,
    IconButton, Tooltip as ChakraTooltip, HStack
} from '@chakra-ui/react';
import { MenuItemRow } from '../../../ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList, Cell } from 'recharts';
import { TimeseriesDataPoint, ReportingComparison } from '../../../types/reporting';
import { getTimeseriesComparisonLabel } from '../shared/reportingComparisonLabels';
import { TbChevronDown, TbDownload, TbTrendingUp, TbTrendingDown, TbInfoCircle } from 'react-icons/tb';
import { exportToCsv } from '../../../utils/csvExport';
import { getBarChartYDomain, getBarChartYTicks } from '../../../utils/chartUtils';
import {
    chartValueFontSize,
    comparisonBarLabelListContent,
    stackedSegmentLabelListContent,
    verticalBarTopLabelListProps,
    yAxisTickFontSize,
    yAxisWidth,
} from '../shared/barValueLabels';
import {
    formatPeriodAxisLabel,
    formatPeriodTooltipLabel,
    getPartialPeriodLabel,
    getTimeseriesPlotWidth,
    getTimeseriesXAxisLayout,
    isPartialReportingPeriod,
    resolveComparablePriorPeriodLabel,
} from '../shared/dateUtils';
import { timeseriesXAxisTickRenderer } from '../shared/TimeseriesDateTick';
import { ComparisonTooltipFooter } from '../shared/ComparisonTooltipFooter';
import { hasComparablePriorPeriod, resolveComparablePriorMetricValue } from '../shared/reportingPriorValues';
import {
    filterTimeseriesPointsForDisplay,
    getOverviewTimeseriesActivity,
} from '../shared/reportingTimeseriesVisibility';
import { getSourceColor, getSourceLabel } from '../../../components/SourceBadge';
import { MARKETPLACE_CHART_TOP_MARGIN, MarketplaceChartLegend } from '../shared/MarketplaceChartLegend';
import { useReportingChartFullscreen } from '../shared/reportingChartFullscreen';
import { isStackedMarketplaceMetric } from '../widgets/types';
import { rehydrateOverviewTimeseriesPoint } from '../../../utils/reportingBySource';

// Supported metrics for the overview timeseries
export type OverviewMetric = 
    | 'orderVolume'
    | 'orderVolumeWoPM'
    | 'orderCost'
    | 'orderCount' 
    | 'grossProfit'
    | 'partsOrdered'
    | 'avgOrderSize'
    | 'medianOrderSize'
    | 'avgListPrice'
    | 'medianListPrice'
    | 'avgListPriceWoPM'
    | 'medianListPriceWoPM'
    | 'avgGrossMarginPct'
    | 'medianGrossMarginPct'
    | 'partListPricePctOfOE'
    | 'avgGrossMarginPctWoPM'
    | 'medianGrossMarginPctWoPM'
    | 'grossMarginLossFromPM'
    | 'missedOpportunity'
    | 'avgMissedOpportunity'
    | 'medianMissedOpportunity'
    | 'pctOemVolume'
    | 'pctOemCount'
    | 'pctLkqVolume'
    | 'pctLkqCount'
    | 'pctAftermarketVolume'
    | 'pctAftermarketCount'
    | 'pctPartsFlagged';

export interface MetricOption {
    value: OverviewMetric;
    label: string;
    format: 'currency' | 'percent' | 'number';
    tooltip?: string;
}

const DEFAULT_METRIC_OPTIONS: MetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Average dollar value per order' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: 'Median dollar value per order' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best" for all parts ordered or confirmed. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total "insurance best", divided by the number of orders with at least one eligible part. Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0. Each order contributes its total list price minus its total "insurance best". Insurance best reflects the MCE viable part from PartsTrader or in source parts (availability depends on Yaxle plan)' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', tooltip: 'Percentage of order count from aftermarket parts' },
    { value: 'pctPartsFlagged', label: '% of Parts Flagged', format: 'percent', tooltip: 'Percent of parts that are flagged and held back from being auto ordered' },
];

const METRIC_GROUPS = [
    { label: 'Order Size', avg: 'avgOrderSize', median: 'medianOrderSize' },
    { label: 'List Price', avg: 'avgListPrice', median: 'medianListPrice' },
    { label: 'List Price (w/o PM)', avg: 'avgListPriceWoPM', median: 'medianListPriceWoPM' },
    { label: 'Gross Margin', avg: 'avgGrossMarginPct', median: 'medianGrossMarginPct' },
    { label: 'Gross Margin (w/o PM)', avg: 'avgGrossMarginPctWoPM', median: 'medianGrossMarginPctWoPM' },
    { label: 'Missed Opportunity', avg: 'avgMissedOpportunity', median: 'medianMissedOpportunity' }
] as const;

interface TimeseriesChartProps {
    data: TimeseriesDataPoint[] | undefined;
    isLoading: boolean;
    defaultMetric?: OverviewMetric;
    title: string;
    height?: number | string;
    showMetricSelector?: boolean;
    /** When true, hides title/tooltip/download but keeps metric selectors */
    hideHeader?: boolean;
    granularity?: string;
    /** Inclusive selected range start — clips first partial week/month tooltip dates. */
    periodStartDate?: string;
    /** Inclusive selected range end — clips partial week/biweekly axis labels. */
    periodEndDate?: string;
    comparison?: ReportingComparison;
    preset?: string;
    metricOptions?: MetricOption[];
    showComparisonBars?: boolean;
    /** Segment each period's bar by marketplace (sourceData.source). */
    marketplaceBreakdown?: boolean;
}

const SOURCE_SERIES_PREFIX = 'src__';

export const MarketplaceTooltip = ({ active, payload, metric, metricOption, granularity, sourceKeys, stacked, showPrior, preset, periodStartDate, periodEndDate, comparison }: any) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const isPartial = isPartialReportingPeriod(row.date, granularity, periodEndDate, periodStartDate);
    const partialLabel = getPartialPeriodLabel(granularity);
    const currentPeriodLabel = formatPeriodTooltipLabel(row.date, granularity, periodEndDate, periodStartDate);
    const formatter = getTooltipFormatter(metricOption.format);
    const priorLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'prior';
    const priorPeriodLabel = showPrior
        ? resolveComparablePriorPeriodLabel(
            row.date,
            granularity,
            comparison,
            row.priorDate,
            row.priorEndDate,
            preset,
            periodEndDate,
            periodStartDate,
        )
        : undefined;

    const entries = sourceKeys
        .map((src: string) => ({
            src,
            value: Number(row[`${SOURCE_SERIES_PREFIX}${src}`] ?? 0),
            priorValue: typeof row[`${SOURCE_SERIES_PREFIX}${src}__prior`] === 'number'
                ? row[`${SOURCE_SERIES_PREFIX}${src}__prior`] as number
                : undefined,
        }))
        .filter((e: { value: number; priorValue?: number }) => e.value !== 0 || (showPrior && e.priorValue !== undefined));

    // Prefer parent metric (distinct orders / true bucket total). Summing bySource
    // overcounts when an RO touches multiple marketplaces.
    const total = typeof row[metric] === 'number'
        ? Number(row[metric])
        : entries.reduce((sum: number, e: { value: number }) => sum + e.value, 0);
    const totalLabel = metric === 'orderCount' ? 'Distinct Orders' : 'Total';
    const priorTotal = typeof row.prior?.[metric] === 'number' ? row.prior[metric] as number : undefined;
    const totalChange = typeof row.change?.[metric] === 'number' ? row.change[metric] as number : undefined;
    const totalChangeLabel = metricOption.format === 'percent' ? 'pp' : '%';
    const totalChangePositive = totalChange !== undefined && totalChange >= 0;

    return (
        <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="200px">
            <Text fontSize="xs" color="gray.500" mb={1}>
                {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                {metricOption.label}
            </Text>
            {showPrior && priorPeriodLabel && (
                <Text fontSize="xs" color="gray.500" mb={1}>
                    Prior period: {priorPeriodLabel}
                </Text>
            )}
            <Flex direction="column" gap={1}>
                {entries.map(({ src, value, priorValue }: { src: string; value: number; priorValue?: number }, i: number) => (
                    <Flex key={src} align="center" justify="space-between" gap={4}>
                        <Flex align="center" gap={2}>
                            <Box w="10px" h="10px" borderRadius="sm" bg={getSourceColor(src, i)} flexShrink={0} />
                            <Text fontSize="xs" color="gray.600">{getSourceLabel(src)}</Text>
                        </Flex>
                        <Flex align="center" gap={1}>
                            {showPrior && (
                                <>
                                    <Text fontSize="xs" color="gray.400">
                                        {priorValue === undefined ? '—' : formatter(priorValue)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.300">→</Text>
                                </>
                            )}
                            <Text fontSize="xs" fontWeight="medium" color="gray.800">{formatter(value)}</Text>
                        </Flex>
                    </Flex>
                ))}
            </Flex>
            {stacked && entries.length > 1 && (
                <>
                    <Flex align="center" justify="space-between" mt={2} pt={2} borderTop="1px solid" borderColor="gray.100" gap={2}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">{totalLabel}</Text>
                        <Flex align="center" gap={1}>
                            {showPrior && (
                                <>
                                    <Text fontSize="xs" color="gray.400">
                                        {priorTotal === undefined ? '—' : formatter(priorTotal)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.300">→</Text>
                                </>
                            )}
                            <Text fontSize="xs" fontWeight="bold" color="#0071E3">{formatter(total)}</Text>
                        </Flex>
                    </Flex>
                    {totalChange !== undefined && (
                        <Flex align="center" justify="flex-end" gap={1} mt={1}>
                            <Icon
                                as={totalChangePositive ? TbTrendingUp as React.ElementType : TbTrendingDown as React.ElementType}
                                color={totalChangePositive ? 'green.500' : 'red.500'}
                                boxSize={3}
                            />
                            <Text fontSize="xs" fontWeight="medium" color={totalChangePositive ? 'green.500' : 'red.500'}>
                                {Math.abs(totalChange).toFixed(1)}{totalChangeLabel}
                                <Text as="span" color="gray.400" ml={1} fontWeight="normal">{priorLabel}</Text>
                            </Text>
                        </Flex>
                    )}
                    {showPrior && totalChange === undefined && (
                        <ComparisonTooltipFooter
                            comparison={showPrior}
                            change={totalChange}
                            priorValue={priorTotal}
                            priorPeriodLabel={priorPeriodLabel}
                        />
                    )}
                </>
            )}
            {showPrior && (
                <Text fontSize="10px" color="gray.400" mt={1}>Faded bars = prior period</Text>
            )}
        </Box>
    );
};

/**
 * Format currency with K/M abbreviations
 */
const formatCompactCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000) {
        return `${sign}$${(absValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${sign}$${(absValue / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return `${sign}$${Math.round(absValue).toLocaleString()}`;
};

/**
 * Format number with K/M abbreviations
 */
const formatCompactNumber = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
        return `${(value / 1000000).toFixed(absValue >= 10000000 ? 0 : 1)}M`;
    }
    if (absValue >= 1000) {
        return `${(value / 1000).toFixed(absValue >= 10000 ? 0 : 1)}K`;
    }
    return Math.round(value).toLocaleString();
};

const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;

const getAxisFormatter = (format: 'currency' | 'percent' | 'number'): ((value: number) => string) => {
    switch (format) {
        case 'currency':
            return formatCompactCurrency;
        case 'percent':
            return formatPercent;
        default:
            return formatCompactNumber;
    }
};

// Full formatter for tooltips
const getTooltipFormatter = (format: 'currency' | 'percent' | 'number'): ((value: number) => string) => {
    switch (format) {
        case 'currency':
            return (value: number) => new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'USD', 
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        case 'percent':
            return (value: number) => `${(value || 0).toFixed(1)}%`;
        default:
            return (value: number) => Math.round(value).toLocaleString();
    }
};

export const CustomTooltip = ({ active, payload, metric, metricOption, granularity, comparison, preset, showComparisonBars, periodStartDate, periodEndDate }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const value = data?.[metric] ?? 0;
        const isPartial = isPartialReportingPeriod(data.date, granularity, periodEndDate, periodStartDate);
        const partialLabel = getPartialPeriodLabel(granularity);
        const currentPeriodLabel = formatPeriodTooltipLabel(data.date, granularity, periodEndDate, periodStartDate);
        const priorFromData = typeof data?.priorMetricValue === 'number'
            ? data.priorMetricValue
            : data.prior ? data.prior[metric] : undefined;
        const priorValue = resolveComparablePriorMetricValue(
            typeof priorFromData === 'number' ? priorFromData : undefined,
            {
                comparison,
                date: data.date,
                priorDate: data?.priorDate,
                granularity,
                preset,
            },
        );
        const comparisonLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'vs prior';
        const priorPeriodLabel = comparison
            ? resolveComparablePriorPeriodLabel(
                data.date,
                granularity,
                comparison,
                data?.priorDate,
                data?.priorEndDate,
                preset,
                periodEndDate,
                periodStartDate,
            )
            : undefined;

        const formatter = getTooltipFormatter(metricOption.format);
        
        let change: number | undefined;
        let changeLabel = '';
        
        if (data.change && data.change[metric] !== undefined) {
            change = data.change[metric];
            changeLabel = metricOption.format === 'percent' ? 'pp' : '%';
        }
        
        const isPositive = change !== undefined && change >= 0;
        
        return (
            <Box 
                bg="white" 
                p={3} 
                border="1px solid" 
                borderColor="gray.200" 
                borderRadius="lg" 
                boxShadow="none"
                minW="150px"
            >
                <Text fontSize="xs" color="gray.500" mb={1}>
                    {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    {metricOption.label}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>
                    {formatter(value)}
                </Text>
                {priorValue !== undefined && priorValue !== null && (
                    <>
                        {priorPeriodLabel && (
                            <Text fontSize="xs" color="gray.500">
                                Prior period: {priorPeriodLabel}
                            </Text>
                        )}
                        <Text fontSize="xs" color="gray.500" mb={1}>
                            Prior: {formatter(priorValue)}
                        </Text>
                    </>
                )}
                
                {change !== undefined && (
                    <Flex align="center" gap={1}>
                        <Icon 
                            as={isPositive ? TbTrendingUp as React.ElementType : TbTrendingDown as React.ElementType} 
                            color={isPositive ? "green.500" : "red.500"} 
                            boxSize={3}
                        />
                        <Text 
                            fontSize="xs" 
                            fontWeight="medium" 
                            color={isPositive ? "green.500" : "red.500"}
                        >
                            {Math.abs(change).toFixed(1)}{changeLabel}
                            <Text as="span" color="gray.400" ml={1} fontWeight="normal">
                                {comparisonLabel}
                            </Text>
                        </Text>
                    </Flex>
                )}
                <ComparisonTooltipFooter
                    comparison={comparison}
                    change={change}
                    priorValue={priorValue}
                    priorPeriodLabel={priorPeriodLabel}
                />
            </Box>
        );
    }
    return null;
};

const TimeseriesChart = ({ 
    data, 
    isLoading, 
    defaultMetric = 'orderVolume', 
    title,
    height = 280,
    showMetricSelector = true,
    hideHeader = false,
    granularity,
    periodStartDate,
    periodEndDate,
    comparison,
    preset,
    metricOptions = DEFAULT_METRIC_OPTIONS,
    showComparisonBars = false,
    marketplaceBreakdown = false,
}: TimeseriesChartProps) => {
    const [metric, setMetric] = useState<OverviewMetric>(defaultMetric);
    const [chartWidthRef, chartContainerWidth] = useChartContainerWidth();
    const isFullscreenAxis = useReportingChartFullscreen();
    const axisWidth = yAxisWidth(isFullscreenAxis);
    
    // Sync internal state when controlled defaultMetric changes (e.g. from widget edit)
    useEffect(() => {
        if (defaultMetric) setMetric(defaultMetric);
    }, [defaultMetric]);

    const metricOption = metricOptions.find(o => o.value === metric) || metricOptions[0];
    const axisFormatter = getAxisFormatter(metricOption.format);

    // Identify if the current metric is part of a group
    const activeGroup = METRIC_GROUPS.find(g => 
        (g.avg === metric || g.median === metric) &&
        // Ensure both options exist in the current metricOptions
        metricOptions.some(o => o.value === g.avg) &&
        metricOptions.some(o => o.value === g.median)
    );

    // Prepare display options (grouping Avg/Median)
    const displayOptions = metricOptions.reduce((acc, option) => {
        const group = METRIC_GROUPS.find(g => g.avg === option.value || g.median === option.value);
        
        // If part of a valid group (both variants exist)
        if (group && metricOptions.some(o => o.value === group.avg) && metricOptions.some(o => o.value === group.median)) {
            // Only add group once
            if (!acc.find(o => o.isGroup && o.groupLabel === group.label)) {
                acc.push({
                    isGroup: true,
                    groupLabel: group.label,
                    value: group.avg!, // Default key, but we select the group
                    group: group,
                    tooltip: option.tooltip, // Take first tooltip
                    // Store reference to the currently selected metric if it's in this group
                    isSelected: metric === group.avg || metric === group.median
                });
            }
        } else {
            // Standalone option
            acc.push({
                isGroup: false,
                ...option,
                isSelected: metric === option.value
            });
        }
        return acc;
    }, [] as any[]);

    const includePriorOnlyBuckets = showComparisonBars && !!comparison;
    const chartData = useMemo(() => {
        if (!data?.length) return [];
        const visibleData = filterTimeseriesPointsForDisplay(
            data.map(rehydrateOverviewTimeseriesPoint),
            { includePriorOnlyBuckets },
            getOverviewTimeseriesActivity,
        );
        return visibleData.map((point) => {
            const priorFromBackend = point.prior?.[metric as keyof NonNullable<typeof point.prior>];
            const priorContext = {
                comparison,
                date: point.date,
                priorDate: point.priorDate,
                granularity: granularity ?? 'week',
                preset,
            };
            return {
                ...point,
                displayDate: formatPeriodAxisLabel(point.date, granularity ?? 'week', periodEndDate, periodStartDate),
                priorMetricValue: resolveComparablePriorMetricValue(
                    typeof priorFromBackend === 'number' ? priorFromBackend : undefined,
                    priorContext,
                ),
            };
        });
    }, [data, includePriorOnlyBuckets, metric, comparison, granularity, preset, periodEndDate, periodStartDate]);

    const animateBars = useAnimateOnDataChange(data);

    if (isLoading && (!data || data.length === 0)) {
        return (
            <Flex align="center" justify="center" w="100%" h="100%" p={2}>
                <Skeleton height="100%" width="100%" borderRadius="lg" />
            </Flex>
        );
    }

    if (!data || data.length === 0 || chartData.length === 0) {
        const emptyHeight = typeof height === 'number' ? `${height}px` : height;
        return (
            <Box h={emptyHeight} display="flex" alignItems="center" justifyContent="center">
                <Text color="gray.500">No data available</Text>
            </Box>
        );
    }

    const hasComparisonBarData =
        showComparisonBars && chartData.some((d) => typeof d.priorMetricValue === 'number' && !isNaN(d.priorMetricValue));
    const values = chartData
        .flatMap((d) => [
            (d as any)[metric] || 0,
            ...(hasComparisonBarData && typeof d.priorMetricValue === 'number' ? [d.priorMetricValue] : []),
        ])
        .filter(v => !isNaN(v));
    const dataMin = values.length ? Math.min(...values) : 0;
    const dataMax = values.length ? Math.max(...values) : 0;
    const minValue = Math.min(dataMin, 0);

    const isPercent = metricOption.format === 'percent';
    const effectiveGranularity = granularity ?? 'week';
    const axisLabels = chartData.map((row) => String(row.displayDate ?? ''));
    const maxAxisLabelLen = axisLabels.reduce((max, label) => Math.max(max, label.length), 0);
    const plotWidthPx = chartContainerWidth > 0
        ? getTimeseriesPlotWidth(chartContainerWidth, axisWidth)
        : undefined;
    const xAxisLayout = getTimeseriesXAxisLayout(
        effectiveGranularity,
        chartData.length,
        maxAxisLabelLen,
        isFullscreenAxis,
        plotWidthPx,
        axisLabels,
    );
    const marketplaceValueFont = chartValueFontSize(isFullscreenAxis);
    const singleValueFont = chartValueFontSize(isFullscreenAxis, true, 11);
    const timeseriesYAxisFont = yAxisTickFontSize(isFullscreenAxis, 11);
    // Plot px height (chart minus top/bottom margins + x-axis band) so below-bar negative
    // labels clear the category axis — same approach as ranking charts.
    const timeseriesBarTopMargin = 32;
    const plotHeightPx = typeof height === 'string'
        ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
        : Math.max(0, height - timeseriesBarTopMargin - xAxisLayout.bottom - xAxisLayout.height);
    const [yMin, yMax] = getBarChartYDomain(dataMin, dataMax, {
        format: metricOption.format,
        capPercentMaxAt100: false,
    }, plotHeightPx);


    // ---- Marketplace breakdown series ----
    const isStacked = isStackedMarketplaceMetric(metric);
    const sourceKeys = marketplaceBreakdown
        ? Array.from(
            chartData.reduce((set: Set<string>, d) => {
                const bs = (d as any).bySource as Record<string, any> | undefined;
                if (bs) Object.keys(bs).forEach((k) => set.add(k));
                const priorBs = (d as any).prior?.bySource as Record<string, any> | undefined;
                if (priorBs) Object.keys(priorBs).forEach((k) => set.add(k));
                return set;
            }, new Set<string>())
        ).sort((a, b) => {
            const totalFor = (src: string) =>
                chartData.reduce((sum, d) => sum + (Number((d as any).bySource?.[src]?.[metric]) || 0), 0);
            return totalFor(b) - totalFor(a);
        })
        : [];
    const useMarketplace = marketplaceBreakdown && sourceKeys.length > 0;

    // Overlay the prior-period marketplace breakdown when prior bars are enabled
    // and the backend provided per-source prior data.
    const showMarketplacePrior =
        useMarketplace &&
        showComparisonBars &&
        !!comparison &&
        chartData.some((d) => hasComparablePriorPeriod({
            comparison,
            date: d.date,
            priorDate: (d as any).priorDate,
            granularity: granularity ?? 'week',
            preset,
        }) || (d as any).prior?.bySource);

    const marketplaceChartData = useMarketplace
        ? chartData.map((d) => {
            const row: Record<string, any> = { ...d };
            sourceKeys.forEach((src) => {
                row[`${SOURCE_SERIES_PREFIX}${src}`] = Number((d as any).bySource?.[src]?.[metric]) || 0;
                if (showMarketplacePrior) {
                    const priorSourceValue = (d as any).prior?.bySource?.[src]?.[metric];
                    row[`${SOURCE_SERIES_PREFIX}${src}__prior`] = resolveComparablePriorMetricValue(
                        typeof priorSourceValue === 'number' ? priorSourceValue : undefined,
                        {
                            comparison,
                            date: d.date,
                            priorDate: (d as any).priorDate,
                            granularity: granularity ?? 'week',
                            preset,
                        },
                    );
                }
            });
            return row;
        })
        : [];

    let mpMin = 0;
    let mpMax = 0;
    if (useMarketplace) {
        const accumulate = (vals: number[]) => {
            if (isStacked) {
                const positiveSum = vals.filter((v) => v > 0).reduce((a, b) => a + b, 0);
                const negativeSum = vals.filter((v) => v < 0).reduce((a, b) => a + b, 0);
                mpMax = Math.max(mpMax, positiveSum);
                mpMin = Math.min(mpMin, negativeSum);
            } else {
                vals.forEach((v) => {
                    mpMax = Math.max(mpMax, v);
                    mpMin = Math.min(mpMin, v);
                });
            }
        };
        marketplaceChartData.forEach((row) => {
            accumulate(sourceKeys.map((src) => Number(row[`${SOURCE_SERIES_PREFIX}${src}`]) || 0));
            if (showMarketplacePrior) {
                accumulate(sourceKeys.flatMap((src) => {
                    const value = row[`${SOURCE_SERIES_PREFIX}${src}__prior`];
                    return typeof value === 'number' ? [value] : [];
                }));
            }
        });
    }
    const [mpYMin, mpYMax] = getBarChartYDomain(mpMin, mpMax, {
        format: metricOption.format,
        capPercentMaxAt100: false,
    }, plotHeightPx);

    const handleDownload = () => {
        if (!data) return;
        exportToCsv(data, `${title.replace(/\s+/g, '_').toLowerCase()}_data`);
    };

    const showHeaderBar = !hideHeader || showMetricSelector;
    const isFlexHeight = typeof height === 'string';
    const containerHeight = isFlexHeight ? '100%' : height;

    return (
        <Box
            h={isFlexHeight ? height : undefined}
            display={isFlexHeight ? 'flex' : undefined}
            flexDirection={isFlexHeight ? 'column' : undefined}
            flex={isFlexHeight ? 1 : undefined}
            minH={isFlexHeight ? 0 : undefined}
            minW={0}
            w="100%"
        >
            {showHeaderBar && <Flex align="center" mb={3} wrap="wrap" gap={2} bg="gray.50" borderRadius="md" px={3} py={2}>
                {!hideHeader && (
                    <>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                            {activeGroup ? activeGroup.label : metricOption.label.replace(/\s*\([^)]+\)\s*/g, '')}
                        </Text>
                        {metricOption.tooltip && (
                            <ChakraTooltip label={metricOption.tooltip} placement="top" hasArrow>
                                <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help">
                                    <Icon as={TbInfoCircle as React.ElementType} boxSize={4} />
                                </Box>
                            </ChakraTooltip>
                        )}
                        <IconButton
                            aria-label="Download CSV"
                            icon={<Icon as={TbDownload as React.ElementType} />}
                            size="sm"
                            h="32px"
                            minW="32px"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'gray.600', bg: 'gray.100' }}
                            onClick={handleDownload}
                        />
                    </>
                )}
                {showMetricSelector && (
                    <HStack ml="auto" spacing={2}>
                        {/* Aggregation Selector (Avg vs Median) - only if active group */}
                        {activeGroup && (
                            <Menu placement="bottom-start">
                                <MenuButton
                                    as={Button}
                                    variant="outline"
                                    bg="white"
                                    borderColor="gray.300"
                                    fontWeight="normal"
                                    size="sm"
                                    h="32px"
                                    px={3}
                                    minW="90px"
                                    textAlign="left"
                                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                                    _hover={{ borderColor: 'gray.400' }}
                                    _active={{ bg: 'white' }}
                                >
                                    <Text fontSize="xs" noOfLines={1}>
                                        {metric === activeGroup.avg ? 'Avg.' : 'Median'}
                                    </Text>
                                </MenuButton>
                                <MenuList p="6px" minW="100px" zIndex={2000}>
                                    <MenuItemRow
                                        kind="single"
                                        isSelected={metric === activeGroup.avg}
                                        onClick={() => { if (activeGroup.avg) setMetric(activeGroup.avg); }}
                                    >
                                        Avg.
                                    </MenuItemRow>
                                    <MenuItemRow
                                        kind="single"
                                        isSelected={metric === activeGroup.median}
                                        onClick={() => { if (activeGroup.median) setMetric(activeGroup.median); }}
                                    >
                                        Median
                                    </MenuItemRow>
                                </MenuList>
                            </Menu>
                        )}

                        {/* Main Metric Selector */}
                        <Menu placement="bottom-start">
                            <MenuButton
                                as={Button}
                                variant="outline"
                                bg="white"
                                borderColor="gray.300"
                                fontWeight="normal"
                                size="sm"
                                h="32px"
                                px={3}
                                w="200px"
                                textAlign="left"
                                rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} />}
                                _hover={{ borderColor: 'gray.400' }}
                                _active={{ bg: 'white' }}
                            >
                                <Text fontSize="xs" noOfLines={1}>
                                    {activeGroup ? activeGroup.label : metricOption.label}
                                </Text>
                            </MenuButton>
                            <MenuList p="6px" minW="200px" zIndex={2000} maxH="350px" overflowY="auto">
                                {displayOptions.map((option: any) => {
                                    const isSelected = option.isSelected;
                                    const optionLabel = option.isGroup ? option.groupLabel : option.label;
                                    
                                    return (
                                        <MenuItemRow
                                            key={option.isGroup ? option.groupLabel : option.value}
                                            kind="single"
                                            isSelected={isSelected}
                                            onClick={() => {
                                                if (option.isGroup) {
                                                    setMetric(option.group.avg);
                                                } else {
                                                    setMetric(option.value);
                                                }
                                            }}
                                        >
                                            {optionLabel}
                                            {option.tooltip ? (
                                                <ChakraTooltip label={option.tooltip} placement="right" hasArrow>
                                                    <Box
                                                        as="span"
                                                        display="inline-flex"
                                                        ml={2}
                                                        verticalAlign="middle"
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                    >
                                                        <Icon as={TbInfoCircle as React.ElementType} boxSize={3} />
                                                    </Box>
                                                </ChakraTooltip>
                                            ) : null}
                                        </MenuItemRow>
                                    );
                                })}
                            </MenuList>
                        </Menu>
                    </HStack>
                )}
            </Flex>}
            <Box
                ref={chartWidthRef}
                position="relative"
                flex={isFlexHeight ? 1 : undefined}
                minH={isFlexHeight ? 0 : undefined}
                minW={0}
                w="100%"
            >
            {useMarketplace && <MarketplaceChartLegend sourceKeys={sourceKeys} isFullscreen={isFullscreenAxis} />}
            <ResponsiveContainer width="100%" height={containerHeight}>
                {useMarketplace ? (
                    <BarChart
                        data={marketplaceChartData}
                        margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 10, left: 0, bottom: xAxisLayout.bottom }}
                        barCategoryGap={isStacked ? '20%' : '16%'}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                            dataKey="displayDate"
                            tickLine={false}
                            axisLine={{ stroke: '#E2E8F0' }}
                            angle={xAxisLayout.angle}
                            textAnchor={xAxisLayout.textAnchor}
                            height={xAxisLayout.height}
                            tickMargin={xAxisLayout.tickMargin}
                            padding={{ left: 0, right: xAxisLayout.labelPadX }}
                            interval={0}
                            tick={timeseriesXAxisTickRenderer(xAxisLayout, marketplaceChartData.length, chartContainerWidth)}
                        />
                        <YAxis
                            domain={[mpYMin, mpYMax]}
                            ticks={getBarChartYTicks(mpYMin, mpYMax)}
                            tick={{ fontSize: timeseriesYAxisFont, fill: '#718096' }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => (v === 0 && mpYMin >= 0 ? '' : axisFormatter(v))}
                            width={axisWidth}
                            interval={0}
                            allowDecimals={isPercent}
                        />
                        <Tooltip
                            content={<MarketplaceTooltip metric={metric} metricOption={metricOption} granularity={granularity} sourceKeys={sourceKeys} stacked={isStacked} showPrior={showMarketplacePrior} preset={preset} periodStartDate={periodStartDate} periodEndDate={periodEndDate} comparison={comparison} />}
                            cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                        />
                        {mpYMin < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                        {/*
                          * Render all prior bars first, then all current bars. For stacked
                          * metrics this yields a prior stack (left) and current stack (right).
                          * For clustered metrics this groups every marketplace's prior bar
                          * together, then every marketplace's current bar together.
                          */}
                        {showMarketplacePrior && sourceKeys.map((src, i) => {
                            const isLast = i === sourceKeys.length - 1;
                            const priorDataKey = `${SOURCE_SERIES_PREFIX}${src}__prior`;
                            return (
                                <Bar
                                    key={`${src}__prior`}
                                    dataKey={priorDataKey}
                                    name={`${getSourceLabel(src)} (prior)`}
                                    legendType="none"
                                    fill={getSourceColor(src, i)}
                                    fillOpacity={0.4}
                                    stackId={isStacked ? 'mp_prior' : undefined}
                                    radius={isStacked ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                                    isAnimationActive={animateBars}
                                >
                                    <LabelList
                                        dataKey={priorDataKey}
                                        {...(isStacked
                                            ? { content: stackedSegmentLabelListContent(axisFormatter, marketplaceValueFont) }
                                            : { content: comparisonBarLabelListContent(axisFormatter, marketplaceValueFont, true) })}
                                    />
                                </Bar>
                            );
                        })}
                        {sourceKeys.map((src, i) => {
                            const isLast = i === sourceKeys.length - 1;
                            const dataKey = `${SOURCE_SERIES_PREFIX}${src}`;
                            return (
                                <Bar
                                    key={src}
                                    dataKey={dataKey}
                                    name={getSourceLabel(src)}
                                    fill={getSourceColor(src, i)}
                                    stackId={isStacked ? 'mp' : undefined}
                                    radius={isStacked ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                                    isAnimationActive={animateBars}
                                >
                                    <LabelList
                                        dataKey={dataKey}
                                        {...(isStacked
                                            ? { content: stackedSegmentLabelListContent(axisFormatter, marketplaceValueFont) }
                                            : showMarketplacePrior
                                                ? { content: comparisonBarLabelListContent(axisFormatter, marketplaceValueFont, true) }
                                                : verticalBarTopLabelListProps(axisFormatter, marketplaceValueFont, true))}
                                    />
                                </Bar>
                            );
                        })}
                    </BarChart>
                ) : (
                <BarChart
                    data={chartData}
                    margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 10, left: 0, bottom: xAxisLayout.bottom }}
                    barGap={hasComparisonBarData ? 4 : undefined}
                    barCategoryGap={hasComparisonBarData ? '20%' : undefined}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis 
                        dataKey="displayDate" 
                        tickLine={false}
                        axisLine={{ stroke: '#E2E8F0' }}
                        angle={xAxisLayout.angle}
                        textAnchor={xAxisLayout.textAnchor}
                        height={xAxisLayout.height}
                        tickMargin={xAxisLayout.tickMargin}
                        padding={{ left: 0, right: xAxisLayout.labelPadX }}
                        interval={0}
                        tick={timeseriesXAxisTickRenderer(xAxisLayout, chartData.length, chartContainerWidth)}
                    />
                    <YAxis 
                        domain={[yMin, yMax]}
                        ticks={getBarChartYTicks(yMin, yMax)}
                        tick={{ fontSize: timeseriesYAxisFont, fill: '#718096' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => (v === 0 && yMin >= 0 ? '' : axisFormatter(v))}
                        width={axisWidth}
                        interval={0}
                        allowDecimals={isPercent}
                    />
                    <Tooltip 
                        content={<CustomTooltip metric={metric} metricOption={metricOption} granularity={granularity} comparison={comparison} preset={preset} showComparisonBars={hasComparisonBarData} periodStartDate={periodStartDate} periodEndDate={periodEndDate} />}
                        cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                    />
                    {/* Reference line at 0 if range includes negative */}
                    {minValue < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                    {hasComparisonBarData && (
                        <Bar
                            dataKey="priorMetricValue"
                            fill="#CBD5E0"
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={animateBars}
                        >
                            <LabelList
                                dataKey="priorMetricValue"
                                content={comparisonBarLabelListContent(axisFormatter, singleValueFont)}
                            />
                        </Bar>
                    )}
                    <Bar 
                        dataKey={metric} 
                        fill="#0071E3"
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={animateBars}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill="#0071E3" />
                        ))}
                        <LabelList 
                            dataKey={metric}
                            {...(hasComparisonBarData
                                ? { content: comparisonBarLabelListContent(axisFormatter, singleValueFont) }
                                : verticalBarTopLabelListProps(axisFormatter, singleValueFont))}
                        />
                    </Bar>
                </BarChart>
                )}
            </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default TimeseriesChart;
